import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { ShoppingCart, Loader2, Check } from 'lucide-react';
import { creditApi } from '@/services/api';
import { loadStripe } from '@stripe/stripe-js';

interface BuyCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Bulk discount tiers
const bulkDiscountTiers = [
  { minCredits: 1, discountPercent: 0 },      // 1-99 credits: $0.10 each
  { minCredits: 100, discountPercent: 5 },    // 100-499 credits: 5% discount
  { minCredits: 500, discountPercent: 10 },   // 500-999 credits: 10% discount
  { minCredits: 1000, discountPercent: 15 },  // 1000+ credits: 15% discount
];

const calculateDiscountedPrice = (credits: number) => {
  const basePrice = credits / 10; // 10 credits = $1

  // Special case: Big Boss Package gets 16% discount
  if (credits === 100000) {
    return basePrice * (1 - 16 / 100);
  }

  // Find applicable discount tier
  let discountPercent = 0;
  for (let i = bulkDiscountTiers.length - 1; i >= 0; i--) {
    if (credits >= bulkDiscountTiers[i].minCredits) {
      discountPercent = bulkDiscountTiers[i].discountPercent;
      break;
    }
  }

  return basePrice * (1 - discountPercent / 100);
};

const getDiscountPercent = (credits: number) => {
  if (credits === 100000) return 16;

  for (let i = bulkDiscountTiers.length - 1; i >= 0; i--) {
    if (credits >= bulkDiscountTiers[i].minCredits) {
      return bulkDiscountTiers[i].discountPercent;
    }
  }
  return 0;
};

// Convert slider value (0-100) to credit amount
const sliderToCredits = (sliderValue: number) => {
  if (sliderValue <= 50) {
    return Math.round(1 + (sliderValue / 50) * 999);
  } else {
    const normalizedValue = (sliderValue - 50) / 50;
    const exponentialValue = Math.pow(normalizedValue, 2.5);
    return Math.round(1000 + exponentialValue * 99000);
  }
};

// Convert credit amount to slider value
const creditsToSlider = (credits: number) => {
  if (credits <= 1000) {
    return Math.round(((credits - 1) / 999) * 50);
  } else {
    const normalizedCredits = (credits - 1000) / 99000;
    const sliderValue = Math.pow(normalizedCredits, 1 / 2.5);
    return Math.round(50 + sliderValue * 50);
  }
};

const creditPackages = [
  { id: 'starter', name: 'Starter Pack', credits: 100, recommended: false },
  { id: 'pro', name: 'Pro Pack', credits: 500, recommended: true },
  { id: 'max', name: 'Boss Pack', credits: 1000, recommended: false },
];

export function BuyCreditsModal({ open, onOpenChange }: BuyCreditsModalProps) {
  const [purchasing, setPurchasing] = useState(false);
  const [sliderValue, setSliderValue] = useState(2); // Start at about 50 credits
  const [customCreditAmount, setCustomCreditAmount] = useState(sliderToCredits(2));
  const [useMaxCredits, setUseMaxCredits] = useState(false);

  const getEffectiveCreditAmount = () => {
    return useMaxCredits ? 100000 : customCreditAmount;
  };

  const handleCreditPurchase = async (packageType: string) => {
    try {
      setPurchasing(true);
      const response = await creditApi.purchaseCredits(packageType);

      // Load Stripe and redirect to checkout
      let stripe = await loadStripe('pk_live_51HmQQxJLxKssJyu66slQWOnqolbwqzUeDxHabUfYXLylNdSUwDF3exVA4c7uN5EepIrwKPY599CDdPkZjIkIZ3JN00w35T2T3Q');
      if (import.meta.env.DEV) {
        stripe = await loadStripe('pk_test_51HmQQxJLxKssJyu6vtlQ7aN9uaEiEhLP6HJtDsbWXxYDbhpkvdgu02xGIuM4SVQ48qrGQVFK55ZujfFK4AGP1xG700XQVYcZq6');
      }

      if (stripe && response.sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId: response.sessionId });
        if (error) {
          console.error('Stripe error:', error);
          alert('Failed to redirect to payment. Please try again.');
        }
      }
    } catch (error) {
      console.error('Credit purchase failed:', error);
      alert('Failed to process credit purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCustomCreditPurchase = async () => {
    try {
      setPurchasing(true);
      const effectiveAmount = getEffectiveCreditAmount();
      const response = await creditApi.purchaseCredits('custom', effectiveAmount);

      // Load Stripe and redirect to checkout
      let stripe = await loadStripe('pk_live_51HmQQxJLxKssJyu66slQWOnqolbwqzUeDxHabUfYXLylNdSUwDF3exVA4c7uN5EepIrwKPY599CDdPkZjIkIZ3JN00w35T2T3Q');
      if (import.meta.env.DEV) {
        stripe = await loadStripe('pk_test_51HmQQxJLxKssJyu6vtlQ7aN9uaEiEhLP6HJtDsbWXxYDbhpkvdgu02xGIuM4SVQ48qrGQVFK55ZujfFK4AGP1xG700XQVYcZq6');
      }

      if (stripe && response.sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId: response.sessionId });
        if (error) {
          console.error('Stripe error:', error);
          alert('Failed to redirect to payment. Please try again.');
        }
      }
    } catch (error) {
      console.error('Custom credit purchase failed:', error);
      alert('Failed to process credit purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-green-600" />
            Buy Credits
          </DialogTitle>
          <DialogDescription>
            Choose a package or customize your credit amount. Credits never expire!
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {/* Custom Credit Amount */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg">Custom Amount</h3>
                <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                  Flexible
                </Badge>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2 text-sm">
                  <span className="text-gray-600">
                    Credits: {getEffectiveCreditAmount().toLocaleString()}
                  </span>
                  <span className="text-gray-600">
                    Price: ${calculateDiscountedPrice(getEffectiveCreditAmount()).toFixed(2)}
                    {getDiscountPercent(getEffectiveCreditAmount()) > 0 && (
                      <span className="ml-1 text-green-600 font-semibold">
                        ({getDiscountPercent(getEffectiveCreditAmount())}% off)
                      </span>
                    )}
                  </span>
                </div>

                {/* Max Credits Checkbox */}
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="maxCreditsCheckbox"
                    checked={useMaxCredits}
                    onChange={(e) => setUseMaxCredits(e.target.checked)}
                    className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="maxCreditsCheckbox" className="text-sm font-medium text-gray-700">
                    🎯 Big Boss Package (100,000) - ${calculateDiscountedPrice(100000).toFixed(2)}
                    <span className="ml-1 text-green-600 font-semibold">
                      ({getDiscountPercent(100000)}% off)
                    </span>
                  </label>
                </div>

                {/* Slider */}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderValue}
                  onChange={(e) => {
                    const newSliderValue = parseInt(e.target.value);
                    setSliderValue(newSliderValue);
                    setCustomCreditAmount(sliderToCredits(newSliderValue));
                  }}
                  disabled={useMaxCredits}
                  className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer ${
                    useMaxCredits ? 'opacity-50' : ''
                  }`}
                />

                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 credit ($0.10)</span>
                  <span>100,000 credits (16% off)</span>
                </div>

                {useMaxCredits && (
                  <div className="text-center text-xs text-purple-600 font-semibold mt-1">
                    🎯 Big Boss Package: 100,000 credits - 16% OFF!
                  </div>
                )}
              </div>

              {/* Input and Price Display */}
              <div className="flex items-center justify-between mb-3">
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={useMaxCredits ? 100000 : customCreditAmount}
                  onChange={(e) => {
                    if (useMaxCredits) return;
                    const newCredits = Math.max(1, Math.min(100000, parseInt(e.target.value) || 1));
                    setCustomCreditAmount(newCredits);
                    setSliderValue(creditsToSlider(newCredits));
                  }}
                  disabled={useMaxCredits}
                  className={`w-32 px-3 py-2 rounded-md border text-center ${
                    useMaxCredits ? 'opacity-50 bg-gray-100' : 'bg-white border-gray-300'
                  }`}
                />
                <div className="text-right">
                  <div className="text-lg font-bold">
                    ${calculateDiscountedPrice(getEffectiveCreditAmount()).toFixed(2)}
                    {getDiscountPercent(getEffectiveCreditAmount()) > 0 && (
                      <span className="ml-1 text-xs text-gray-500 line-through">
                        ${(getEffectiveCreditAmount() / 10).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    ${(calculateDiscountedPrice(getEffectiveCreditAmount()) / getEffectiveCreditAmount()).toFixed(3)}/credit
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCustomCreditPurchase}
                disabled={purchasing}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Buy {getEffectiveCreditAmount().toLocaleString()} Credits
                  </>
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500">Or choose a preset package</span>
              </div>
            </div>

            {/* Preset Packages */}
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative p-6 rounded-lg border-2 ${
                  pkg.recommended
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                {pkg.recommended && (
                  <Badge className="absolute -top-2 left-4 bg-green-500 text-white">
                    Recommended
                  </Badge>
                )}
                {getDiscountPercent(pkg.credits) > 0 && (
                  <Badge className="absolute -top-2 right-4 bg-red-500 text-white">
                    {getDiscountPercent(pkg.credits)}% OFF
                  </Badge>
                )}

                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{pkg.name}</h3>
                    <p className={`text-sm ${pkg.recommended ? 'text-green-800' : 'text-gray-600'}`}>
                      {pkg.credits} credits
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      ${calculateDiscountedPrice(pkg.credits).toFixed(2)}
                      {getDiscountPercent(pkg.credits) > 0 && (
                        <span className="ml-1 text-lg text-gray-500 line-through">
                          ${(pkg.credits / 10).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      ${(calculateDiscountedPrice(pkg.credits) / pkg.credits).toFixed(3)}/credit
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => handleCreditPurchase(pkg.id)}
                  disabled={purchasing}
                  className={`w-full ${
                    pkg.recommended
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {purchasing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>Buy {pkg.name}</>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="bg-gray-100 rounded-md p-3 text-sm text-gray-600 border-t">
          <p>Credits never expire and can be used for any AI-powered patent analysis features.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
